import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { format, differenceInDays, eachDayOfInterval, parseISO, isSameDay } from 'date-fns';
import { toast } from 'sonner';
import { CalendarIcon, Clock, Users, Fuel, Settings2, Check, ArrowLeft, Loader2, Upload, Baby, CreditCard, FileText, Banknote } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CHILD_SEAT_PRICE = 5.0; // €5 per day

// Generate time slots from 08:00 to 20:00
const TIME_SLOTS = [];
for (let h = 8; h <= 20; h++) {
  TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:00`);
  if (h < 20) {
    TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:30`);
  }
}

export default function CarDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  const [car, setCar] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bookingLoading, setBookingLoading] = useState(false);
  
  const [pickupDate, setPickupDate] = useState(null);
  const [returnDate, setReturnDate] = useState(null);
  const [pickupTime, setPickupTime] = useState('09:00');
  const [returnTime, setReturnTime] = useState('09:00');
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  
  // New fields
  const [childSeat, setChildSeat] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('card');
  const [idCardFile, setIdCardFile] = useState(null);
  const [licenseFile, setLicenseFile] = useState(null);
  const [idCardUploading, setIdCardUploading] = useState(false);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const [idCardFileId, setIdCardFileId] = useState(null);
  const [licenseFileId, setLicenseFileId] = useState(null);
  
  const idCardInputRef = useRef(null);
  const licenseInputRef = useRef(null);
  
  // Booked dates state
  const [bookedDates, setBookedDates] = useState([]);
  const [durationDiscounts, setDurationDiscounts] = useState([]);

  useEffect(() => {
    const fetchCar = async () => {
      try {
        const response = await axios.get(`${API}/cars/${id}`);
        setCar(response.data);
      } catch (error) {
        console.error('Error fetching car:', error);
        toast.error('Car not found');
        navigate('/fleet');
      } finally {
        setLoading(false);
      }
    };
    fetchCar();
  }, [id, navigate]);

  // Fetch booked dates for this car
  useEffect(() => {
    const fetchBookedDates = async () => {
      try {
        const response = await axios.get(`${API}/cars/${id}/booked-dates`);
        const ranges = response.data.booked_dates || [];
        const allDates = [];
        ranges.forEach(range => {
          try {
            const start = parseISO(range.start);
            const end = parseISO(range.end);
            const days = eachDayOfInterval({ start, end });
            allDates.push(...days);
          } catch (e) {
            console.error('Error parsing date range:', e);
          }
        });
        setBookedDates(allDates);
      } catch (error) {
        console.error('Error fetching booked dates:', error);
      }
    };
    fetchBookedDates();
  }, [id]);

  // Fetch duration discount rules
  useEffect(() => {
    const fetchDurationDiscounts = async () => {
      try {
        const response = await axios.get(`${API}/duration-discounts`);
        setDurationDiscounts(response.data || []);
      } catch (error) {
        console.error('Error fetching duration discounts:', error);
      }
    };
    fetchDurationDiscounts();
  }, []);

  const totalDays = pickupDate && returnDate 
    ? Math.max(1, differenceInDays(returnDate, pickupDate))
    : 0;
  
  // Calculate price considering seasonal pricing (May 15 - Sep 15)
  const isSeasonDate = (date) => {
    const m = date.getMonth() + 1; // 1-12
    const d = date.getDate();
    if (m > 5 && m < 9) return true;
    if (m === 5 && d >= 15) return true;
    if (m === 9 && d <= 15) return true;
    return false;
  };

  // Find applicable duration discount
  const activeDurationDiscount = durationDiscounts.find(
    rule => totalDays >= rule.min_days && totalDays <= rule.max_days
  );
  const durationDiscountPercent = activeDurationDiscount ? activeDurationDiscount.discount_percent : 0;

  const carPrice = (() => {
    if (!car || !pickupDate || totalDays === 0) return 0;
    const seasonPrice = car.season_price_per_day || car.price_per_day;
    const carDiscount = car.discount_percent || 0;
    const carMultiplier = 1 - (carDiscount / 100);
    const durationMultiplier = 1 - (durationDiscountPercent / 100);
    let total = 0;
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(pickupDate);
      d.setDate(d.getDate() + i);
      total += isSeasonDate(d) ? seasonPrice : car.price_per_day;
    }
    return total * carMultiplier * durationMultiplier;
  })();

  // Original price before discount (for strikethrough display)
  const carPriceOriginal = (() => {
    if (!car || !pickupDate || totalDays === 0) return 0;
    const seasonPrice = car.season_price_per_day || car.price_per_day;
    let total = 0;
    for (let i = 0; i < totalDays; i++) {
      const d = new Date(pickupDate);
      d.setDate(d.getDate() + i);
      total += isSeasonDate(d) ? seasonPrice : car.price_per_day;
    }
    return total;
  })();

  const hasDiscount = (car?.discount_percent > 0) || durationDiscountPercent > 0;

  // Check if current display should show season price
  const now = new Date();
  const showSeasonPrice = car?.season_price_per_day && car.season_price_per_day !== car.price_per_day;

  const childSeatTotal = childSeat ? CHILD_SEAT_PRICE * totalDays : 0;
  const totalPrice = carPrice + childSeatTotal;

  const handleFileUpload = async (file, type) => {
    if (!file) return null;
    
    const setUploading = type === 'id' ? setIdCardUploading : setLicenseUploading;
    const setFileId = type === 'id' ? setIdCardFileId : setLicenseFileId;
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(`${API}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setFileId(response.data.file_id);
      toast.success(`${type === 'id' ? 'ID Card' : 'License'} uploaded successfully`);
      return response.data.file_id;
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload file');
      return null;
    } finally {
      setUploading(false);
    }
  };

  const handleBooking = async (e) => {
    e.preventDefault();
    
    if (!pickupDate || !returnDate) {
      toast.error('Please select pickup and return dates');
      return;
    }
    
    if (!customerName || !customerEmail || !customerPhone) {
      toast.error('Please fill in all customer information');
      return;
    }
    
    if (!idCardFileId || !licenseFileId) {
      toast.error('Please upload both ID card and driver\'s license');
      return;
    }

    setBookingLoading(true);
    
    try {
      const bookingData = {
        car_id: id,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
        pickup_date: pickupDate.toISOString(),
        return_date: returnDate.toISOString(),
        pickup_time: pickupTime,
        return_time: returnTime,
        child_seat: childSeat,
        id_card_url: idCardFileId,
        license_url: licenseFileId,
        payment_method: paymentMethod,
      };
      
      const response = await axios.post(`${API}/bookings`, bookingData);
      
      if (paymentMethod === 'card') {
        // Proceed to Stripe payment
        const paymentResponse = await axios.post(`${API}/payments/checkout`, {
          booking_id: response.data.id,
          origin_url: window.location.origin,
        });
        
        // Redirect to Stripe
        window.location.href = paymentResponse.data.url;
      } else {
        // Cash or Bank - redirect to confirmation page
        navigate(`/booking/confirmed/${response.data.id}?method=${paymentMethod}`);
      }
      
    } catch (error) {
      console.error('Booking error:', error);
      toast.error('Failed to create booking. Please try again.');
      setBookingLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!car) {
    return null;
  }

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="car-detail-page">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate('/fleet')}
          className="mb-8"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Fleet
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Car Image & Details */}
          <div>
            <div className="relative rounded-2xl overflow-hidden mb-6">
              <img
                src={car.image_url}
                alt={car.name}
                className="w-full h-[400px] object-cover"
              />
              <div className="absolute top-4 left-4">
                <span className="px-4 py-2 bg-white/90 text-sm font-medium rounded-full capitalize">
                  {car.category}
                </span>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h1 className="font-serif text-3xl font-bold">{car.name}</h1>
                  <p className="text-muted-foreground">{car.brand} • {car.year || 2024}</p>
                </div>
                <div className="text-right">
                  {car.discount_percent > 0 ? (
                    <>
                      <span className="text-xl text-muted-foreground line-through">€{car.price_per_day}</span>
                      <span className="text-4xl font-bold text-red-500 ml-1">€{(car.price_per_day * (1 - car.discount_percent / 100)).toFixed(0)}</span>
                      <span className="block text-muted-foreground">{t('perDay')}</span>
                      <span className="inline-block mt-1 px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">-{car.discount_percent}%</span>
                    </>
                  ) : (
                    <>
                      <span className="text-4xl font-bold text-gold">€{car.price_per_day}</span>
                      <span className="block text-muted-foreground">{t('perDay')}</span>
                    </>
                  )}
                  {showSeasonPrice && (
                    <div className="mt-1">
                      {car.discount_percent > 0 ? (
                        <>
                          <span className="text-sm text-muted-foreground line-through">€{car.season_price_per_day}</span>
                          <span className="text-lg font-semibold text-orange-500 ml-1">€{(car.season_price_per_day * (1 - car.discount_percent / 100)).toFixed(0)}</span>
                        </>
                      ) : (
                        <span className="text-lg font-semibold text-orange-500">€{car.season_price_per_day}</span>
                      )}
                      <span className="text-xs text-orange-500 block">{t('seasonPrice')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Specs */}
              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-secondary rounded-lg text-center">
                  <Users className="w-6 h-6 mx-auto mb-2 text-accent" />
                  <span className="block text-sm text-muted-foreground">{t('seats')}</span>
                  <span className="font-semibold">{car.seats}</span>
                </div>
                <div className="p-4 bg-secondary rounded-lg text-center">
                  <Settings2 className="w-6 h-6 mx-auto mb-2 text-accent" />
                  <span className="block text-sm text-muted-foreground">{t('transmission')}</span>
                  <span className="font-semibold capitalize">{car.transmission}</span>
                </div>
                <div className="p-4 bg-secondary rounded-lg text-center">
                  <Fuel className="w-6 h-6 mx-auto mb-2 text-accent" />
                  <span className="block text-sm text-muted-foreground">{t('fuelType')}</span>
                  <span className="font-semibold capitalize">{car.fuel_type}</span>
                </div>
              </div>

              {/* Features */}
              <div>
                <h3 className="font-semibold mb-3">{t('features')}</h3>
                <div className="flex flex-wrap gap-2">
                  {car.features.map((feature, i) => (
                    <span key={i} className="flex items-center gap-1 px-3 py-1.5 bg-secondary rounded-full text-sm">
                      <Check className="w-4 h-4 text-success" />
                      {feature}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <div>
            <div className="bg-card border border-border rounded-xl p-8 sticky top-28">
              <h2 className="font-serif text-2xl font-bold mb-6">{t('booking')}</h2>

              <form onSubmit={handleBooking} className="space-y-6">
                {/* Pickup Date & Time */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {t('pickupDate')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          data-testid="detail-pickup-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {pickupDate ? format(pickupDate, 'PP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={pickupDate}
                          onSelect={setPickupDate}
                          disabled={(date) => {
                            if (date < new Date()) return true;
                            return bookedDates.some(bd => isSameDay(bd, date));
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Select value={pickupTime} onValueChange={setPickupTime}>
                      <SelectTrigger data-testid="pickup-time">
                        <Clock className="mr-2 h-4 w-4" />
                        <SelectValue placeholder="Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Return Date & Time */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <CalendarIcon className="w-4 h-4" />
                    {t('returnDate')}
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className="w-full justify-start"
                          data-testid="detail-return-date"
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {returnDate ? format(returnDate, 'PP') : 'Select date'}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0">
                        <Calendar
                          mode="single"
                          selected={returnDate}
                          onSelect={setReturnDate}
                          disabled={(date) => {
                            if (date < (pickupDate || new Date())) return true;
                            return bookedDates.some(bd => isSameDay(bd, date));
                          }}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                    <Select value={returnTime} onValueChange={setReturnTime}>
                      <SelectTrigger data-testid="return-time">
                        <Clock className="mr-2 h-4 w-4" />
                        <SelectValue placeholder="Time" />
                      </SelectTrigger>
                      <SelectContent>
                        {TIME_SLOTS.map((time) => (
                          <SelectItem key={time} value={time}>
                            {time}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Documents Upload */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide flex items-center gap-2">
                    <FileText className="w-4 h-4" />
                    {t('documents')}
                  </h3>
                  
                  {/* ID Card Upload */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <CreditCard className="w-4 h-4" />
                      {t('idCard')} *
                    </Label>
                    <input
                      type="file"
                      ref={idCardInputRef}
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setIdCardFile(file);
                          handleFileUpload(file, 'id');
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => idCardInputRef.current?.click()}
                      disabled={idCardUploading}
                      data-testid="upload-id-card"
                    >
                      {idCardUploading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      {idCardFile ? (
                        <span className="text-success flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {idCardFile.name}
                        </span>
                      ) : (
                        t('uploadIdCard')
                      )}
                    </Button>
                  </div>

                  {/* License Upload */}
                  <div>
                    <Label className="flex items-center gap-2 mb-2">
                      <FileText className="w-4 h-4" />
                      {t('license')} *
                    </Label>
                    <input
                      type="file"
                      ref={licenseInputRef}
                      className="hidden"
                      accept="image/*,.pdf"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          setLicenseFile(file);
                          handleFileUpload(file, 'license');
                        }
                      }}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => licenseInputRef.current?.click()}
                      disabled={licenseUploading}
                      data-testid="upload-license"
                    >
                      {licenseUploading ? (
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      ) : (
                        <Upload className="mr-2 h-4 w-4" />
                      )}
                      {licenseFile ? (
                        <span className="text-success flex items-center gap-2">
                          <Check className="w-4 h-4" />
                          {licenseFile.name}
                        </span>
                      ) : (
                        t('uploadLicense')
                      )}
                    </Button>
                  </div>
                </div>

                {/* Customer Info */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {t('customerInfo')}
                  </h3>
                  <div>
                    <Label htmlFor="name">{t('fullName')}</Label>
                    <Input
                      id="name"
                      value={customerName}
                      onChange={(e) => setCustomerName(e.target.value)}
                      className="mt-2"
                      required
                      data-testid="customer-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">{t('email')}</Label>
                    <Input
                      id="email"
                      type="email"
                      value={customerEmail}
                      onChange={(e) => setCustomerEmail(e.target.value)}
                      className="mt-2"
                      required
                      data-testid="customer-email"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">{t('phone')}</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={customerPhone}
                      onChange={(e) => setCustomerPhone(e.target.value)}
                      className="mt-2"
                      required
                      data-testid="customer-phone"
                    />
                  </div>
                </div>

                {/* Extras */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {t('extras')}
                  </h3>
                  <div className="flex items-center space-x-3 p-4 bg-secondary rounded-lg">
                    <Checkbox
                      id="childSeat"
                      checked={childSeat}
                      onCheckedChange={setChildSeat}
                      data-testid="child-seat-checkbox"
                    />
                    <div className="flex-1">
                      <Label htmlFor="childSeat" className="flex items-center gap-2 cursor-pointer">
                        <Baby className="w-5 h-5 text-accent" />
                        {t('childSeat')}
                      </Label>
                      <p className="text-sm text-muted-foreground">€{CHILD_SEAT_PRICE} {t('perDay')}</p>
                    </div>
                  </div>
                </div>

                {/* Payment Method */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                    {t('paymentMethod')}
                  </h3>
                  <div className="space-y-3">
                    {/* Card Payment */}
                    <label 
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        paymentMethod === 'card' 
                          ? 'border-accent bg-accent/5' 
                          : 'border-border hover:border-accent/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="card"
                        checked={paymentMethod === 'card'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="hidden"
                      />
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        paymentMethod === 'card' ? 'bg-accent text-white' : 'bg-secondary'
                      }`}>
                        <CreditCard className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{t('cardPayment')}</p>
                        <p className="text-sm text-muted-foreground">{t('cardPaymentDesc')}</p>
                      </div>
                      {paymentMethod === 'card' && <Check className="w-5 h-5 text-accent" />}
                    </label>

                    {/* Cash Payment */}
                    <label 
                      className={`flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        paymentMethod === 'cash' 
                          ? 'border-accent bg-accent/5' 
                          : 'border-border hover:border-accent/50'
                      }`}
                    >
                      <input
                        type="radio"
                        name="paymentMethod"
                        value="cash"
                        checked={paymentMethod === 'cash'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="hidden"
                      />
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        paymentMethod === 'cash' ? 'bg-accent text-white' : 'bg-secondary'
                      }`}>
                        <Banknote className="w-5 h-5" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium">{t('cashPayment')}</p>
                        <p className="text-sm text-muted-foreground">{t('cashPaymentDesc')}</p>
                      </div>
                      {paymentMethod === 'cash' && <Check className="w-5 h-5 text-accent" />}
                    </label>
                  </div>
                </div>

                {/* Duration Discount Info */}
                {durationDiscounts.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4" data-testid="duration-discount-info">
                    <p className="text-sm font-semibold text-red-700 mb-2">{t('durationDiscountTitle')}</p>
                    <div className="flex flex-wrap gap-2">
                      {durationDiscounts.map((d, i) => (
                        <span key={i} className={`text-xs px-3 py-1.5 rounded-full font-medium ${
                          activeDurationDiscount?.id === d.id 
                            ? 'bg-red-500 text-white' 
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {d.min_days}-{d.max_days} {t('totalDays').toLowerCase()}: -{d.discount_percent}%
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Price Summary */}
                {totalDays > 0 && (
                  <div className="bg-secondary rounded-xl p-4 space-y-3">
                    {showSeasonPrice && pickupDate ? (() => {
                      let normalDays = 0, seasonDays = 0;
                      for (let i = 0; i < totalDays; i++) {
                        const d = new Date(pickupDate);
                        d.setDate(d.getDate() + i);
                        if (isSeasonDate(d)) seasonDays++;
                        else normalDays++;
                      }
                      return (
                        <>
                          {normalDays > 0 && (
                            <div className="flex justify-between text-sm">
                              <span>{car.name} × {normalDays} {t('totalDays').toLowerCase()}</span>
                              <span>€{(normalDays * car.price_per_day).toFixed(2)}</span>
                            </div>
                          )}
                          {seasonDays > 0 && (
                            <div className="flex justify-between text-sm text-orange-600">
                              <span>{car.name} × {seasonDays} {t('totalDays').toLowerCase()} ({t('seasonLabel')})</span>
                              <span>€{(seasonDays * car.season_price_per_day).toFixed(2)}</span>
                            </div>
                          )}
                        </>
                      );
                    })() : (
                      <div className="flex justify-between text-sm">
                        <span>{car.name} × {totalDays} {t('totalDays').toLowerCase()}</span>
                        <span>€{carPriceOriginal.toFixed(2)}</span>
                      </div>
                    )}
                    {(car.discount_percent > 0) && (
                      <div className="flex justify-between text-sm text-red-500 font-medium">
                        <span>{t('discount')} (-{car.discount_percent}%)</span>
                        <span>-€{(carPriceOriginal * car.discount_percent / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {durationDiscountPercent > 0 && (
                      <div className="flex justify-between text-sm text-red-500 font-medium">
                        <span>{totalDays} {t('totalDays').toLowerCase()} (-{durationDiscountPercent}%)</span>
                        <span>-€{(carPriceOriginal * (1 - (car.discount_percent || 0) / 100) * durationDiscountPercent / 100).toFixed(2)}</span>
                      </div>
                    )}
                    {childSeat && (
                      <div className="flex justify-between text-sm">
                        <span>{t('childSeat')} × {totalDays} {t('totalDays').toLowerCase()}</span>
                        <span>€{childSeatTotal.toFixed(2)}</span>
                      </div>
                    )}
                    <div className="text-sm text-muted-foreground space-y-1 pt-2 border-t border-border">
                      <div className="flex justify-between">
                        <span>{t('pickupDate')}:</span>
                        <span>{pickupDate && format(pickupDate, 'PP')} {pickupTime}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>{t('returnDate')}:</span>
                        <span>{returnDate && format(returnDate, 'PP')} {returnTime}</span>
                      </div>
                    </div>
                    <div className="border-t border-border pt-2 flex justify-between font-semibold">
                      <span>{t('totalPrice')}</span>
                      <div>
                        {hasDiscount && <span className="text-sm text-muted-foreground line-through mr-2">€{(carPriceOriginal + childSeatTotal).toFixed(2)}</span>}
                        <span className="text-xl text-gold">€{totalPrice.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                )}

                <Button
                  type="submit"
                  className="w-full h-14 bg-accent hover:bg-accent/90 text-white text-lg font-semibold"
                  disabled={bookingLoading || totalDays === 0 || !idCardFileId || !licenseFileId}
                  data-testid="proceed-payment-btn"
                >
                  {bookingLoading ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : paymentMethod === 'card' ? (
                    t('proceedToPayment')
                  ) : (
                    t('confirmBooking')
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
